import {
  addDays,
  addWeeks,
  addMonths,
  subDays,
  subWeeks,
  subMonths,
  parseISO,
  format,
} from 'date-fns'
import type { CalendarMilestoneRead, CalcUnit } from '@/types'

function applyOffset(
  base: Date,
  direction: 'ADD' | 'SUBTRACT',
  quantity: number,
  unit: CalcUnit
): Date {
  const fn =
    direction === 'ADD'
      ? unit === 'DAY'
        ? addDays
        : unit === 'WEEK'
          ? addWeeks
          : addMonths
      : unit === 'DAY'
        ? subDays
        : unit === 'WEEK'
          ? subWeeks
          : subMonths
  return fn(base, quantity)
}

/**
 * Resolves dates for all milestones in a calendar (across all events).
 *
 * SPECIFIC milestones use their specific_date or resolved_date directly.
 * CALCULATED milestones derive their date from calc_from_milestone_id.
 *
 * Returns a Map<milestoneId, resolvedDateString | null>.
 * Null means the date could not be resolved (missing dependency or cycle).
 */
export function resolveAllDates(
  milestones: CalendarMilestoneRead[]
): Map<string, string | null> {
  const resolved = new Map<string, string | null>()
  const byId = new Map(milestones.map(m => [m.id, m]))

  // Track visit state for cycle detection: 0=unvisited, 1=in-progress, 2=done
  const state = new Map<string, number>()

  function resolve(id: string): string | null {
    if (resolved.has(id)) return resolved.get(id)!

    const visitState = state.get(id) ?? 0
    if (visitState === 1) {
      // Cycle detected
      resolved.set(id, null)
      return null
    }

    const ms = byId.get(id)
    if (!ms) {
      resolved.set(id, null)
      return null
    }

    state.set(id, 1) // mark in-progress

    let result: string | null = null

    if (ms.date_mode === 'SPECIFIC') {
      result = ms.specific_date ?? ms.resolved_date ?? null
    } else if (ms.date_mode === 'CALCULATED') {
      if (
        ms.calc_from_milestone_id &&
        ms.calc_direction &&
        ms.calc_quantity != null &&
        ms.calc_unit
      ) {
        const baseDate = resolve(ms.calc_from_milestone_id)
        if (baseDate) {
          try {
            const computed = applyOffset(
              parseISO(baseDate),
              ms.calc_direction,
              ms.calc_quantity,
              ms.calc_unit
            )
            result = format(computed, 'yyyy-MM-dd')
          } catch {
            result = null
          }
        }
      }
    }

    state.set(id, 2) // mark done
    resolved.set(id, result)
    return result
  }

  for (const ms of milestones) {
    resolve(ms.id)
  }

  return resolved
}
